import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell,
} from 'recharts';
import api from '../services/api';
import './PerformanceDashboard.css';

const asList = (data) => (Array.isArray(data) ? data : data?.items || data?.results || []);

const formatDay = (value) => {
  if (!value) return '';
  const [year, month, day] = String(value).split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};

const signed = (value, suffix) => {
  if (value == null || Number.isNaN(Number(value))) return null;
  const number = Number(value);
  if (number === 0) return null;
  const prefix = number > 0 ? '+' : '';
  return `${prefix}${number}${suffix}`;
};

const PerformanceDashboard = () => {
  const [period, setPeriod] = useState('30');
  const [draft, setDraft] = useState({
    disciplineId: '',
    eventId: '',
    groupId: '',
    studentId: '',
    dateFrom: '',
    dateTo: '',
  });
  const [applied, setApplied] = useState(draft);

  const { data: settings } = useQuery('performance-settings', async () => {
    const response = await api.get('/performance/settings');
    return response.data;
  });
  const { data: studentsData } = useQuery('performance-dash-students', async () => {
    const response = await api.get('/students', { params: { is_active: true, limit: 500 } });
    return asList(response.data);
  });
  const { data: groupsData } = useQuery('performance-dash-groups', async () => {
    const response = await api.get('/groups', { params: { is_active: true } });
    return asList(response.data);
  });

  const events = useMemo(
    () =>
      (settings?.disciplines || []).flatMap((item) =>
        (item.events || [])
          .filter((event) => event.id)
          .map((event) => ({
            ...event,
            discipline_id: item.discipline_id,
            discipline_name: item.discipline_name,
          }))
      ),
    [settings]
  );
  const visibleEvents = events.filter((event) =>
    draft.disciplineId ? String(event.discipline_id) === String(draft.disciplineId) : true
  );

  const params = {
    ...(period === 'custom'
      ? {
          ...(applied.dateFrom ? { date_from: applied.dateFrom } : {}),
          ...(applied.dateTo ? { date_to: applied.dateTo } : {}),
        }
      : { period_days: Number(period) }),
    ...(applied.disciplineId ? { discipline_id: applied.disciplineId } : {}),
    ...(applied.eventId ? { event_id: applied.eventId } : {}),
    ...(applied.groupId ? { group_id: applied.groupId } : {}),
    ...(applied.studentId ? { student_id: applied.studentId } : {}),
  };

  const { data, isLoading, error } = useQuery(['performance-dashboard', params], async () => {
    const response = await api.get('/performance/dashboard', { params });
    return response.data;
  });

  const applyFilters = (event) => {
    event.preventDefault();
    setApplied(draft);
  };

  const goalSlices = [
    { name: 'Cumplidos', value: data?.goals_met || 0, color: '#22c55e' },
    { name: 'Pendientes', value: data?.goals_pending || 0, color: '#cbd5e1' },
  ].filter((item) => item.value > 0);

  const effectivenessDelta = signed(data?.effectiveness_delta, '%');
  const goalsDelta = signed(data?.goals_delta, '%');
  const evaluationsDelta = signed(data?.evaluations_delta, '%');

  return (
    <div className="page-container results-dash">
      <div className="results-dash-header">
        <div>
          <h2>Dashboard de resultados</h2>
          <p>Analiza el rendimiento y la evolución de tus alumnos.</p>
        </div>
        <div className="results-dash-range">
          {formatDay(data?.date_from)} – {formatDay(data?.date_to)}
        </div>
      </div>

      <form className="card results-dash-filters" onSubmit={applyFilters}>
        <label>
          Período
          <select
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
          >
            <option value="7">Últimos 7 días</option>
            <option value="30">Últimos 30 días</option>
            <option value="90">Últimos 90 días</option>
            <option value="custom">Personalizado</option>
          </select>
        </label>
        <label>
          Disciplina
          <select
            value={draft.disciplineId}
            onChange={(event) => setDraft((prev) => ({ ...prev, disciplineId: event.target.value, eventId: '' }))}
          >
            <option value="">Todas</option>
            {(settings?.disciplines || []).map((item) => (
              <option key={item.discipline_id} value={item.discipline_id}>{item.discipline_name}</option>
            ))}
          </select>
        </label>
        <label>
          Prueba
          <select value={draft.eventId} onChange={(event) => setDraft((prev) => ({ ...prev, eventId: event.target.value }))}>
            <option value="">Todas</option>
            {visibleEvents.map((event) => (
              <option key={event.id} value={event.id}>{event.name}</option>
            ))}
          </select>
        </label>
        <label>
          Grupo
          <select value={draft.groupId} onChange={(event) => setDraft((prev) => ({ ...prev, groupId: event.target.value }))}>
            <option value="">Todos</option>
            {(groupsData || []).map((group) => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>
        </label>
        <label>
          Alumno
          <select value={draft.studentId} onChange={(event) => setDraft((prev) => ({ ...prev, studentId: event.target.value }))}>
            <option value="">Todos</option>
            {(studentsData || []).map((student) => (
              <option key={student.id} value={student.id}>
                {`${student.first_name || ''} ${student.last_name || ''}`.trim()}
              </option>
            ))}
          </select>
        </label>
        {period === 'custom' && (
          <>
            <label>
              Desde
              <input type="date" value={draft.dateFrom} onChange={(event) => setDraft((prev) => ({ ...prev, dateFrom: event.target.value }))} />
            </label>
            <label>
              Hasta
              <input type="date" value={draft.dateTo} onChange={(event) => setDraft((prev) => ({ ...prev, dateTo: event.target.value }))} />
            </label>
          </>
        )}
        <div className="results-dash-filter-actions">
          <button type="submit" className="btn btn-primary">Filtrar</button>
        </div>
      </form>

      {isLoading && <div className="loading">Cargando resultados...</div>}
      {error && <div className="card results-dash-warn">No se pudo cargar el dashboard de resultados.</div>}

      {data && (
        <>
          <div className="results-kpi-grid">
            <article className="card results-kpi">
              <span>Efectividad promedio</span>
              <strong>{data.effectiveness == null ? '—' : `${data.effectiveness}%`}</strong>
              {effectivenessDelta && <small className={data.effectiveness_delta >= 0 ? 'is-up' : 'is-down'}>{effectivenessDelta} vs. período anterior</small>}
            </article>
            <article className="card results-kpi">
              <span>Objetivos cumplidos</span>
              <strong>{data.goals_pct == null ? '—' : `${data.goals_pct}%`}</strong>
              <div className="results-kpi-bar"><span style={{ width: `${data.goals_pct || 0}%` }} /></div>
              {goalsDelta && <small className={data.goals_delta >= 0 ? 'is-up' : 'is-down'}>{goalsDelta} vs. período anterior</small>}
            </article>
            <article className="card results-kpi">
              <span>Alumnos evaluados</span>
              <strong>{data.students_evaluated}</strong>
              <small>de {data.students_active} activos</small>
            </article>
            <article className="card results-kpi">
              <span>Evaluaciones realizadas</span>
              <strong>{data.evaluations}</strong>
              {evaluationsDelta && <small className={data.evaluations_delta >= 0 ? 'is-up' : 'is-down'}>{evaluationsDelta} vs. período anterior</small>}
            </article>
          </div>

          <div className="results-charts">
            <section className="card">
              <h3>Evolución de la efectividad</h3>
              {data.weeks?.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={data.weeks}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 100]} unit="%" />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="current" name="Período actual" stroke="#2563eb" strokeWidth={2} connectNulls />
                    <Line type="monotone" dataKey="previous" name="Período anterior" stroke="#22c55e" strokeWidth={2} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="results-empty">Sin evaluaciones en este período.</p>
              )}
            </section>
            <section className="card results-donut">
              <h3>Cumplimiento de objetivos</h3>
              {goalSlices.length ? (
                <div className="results-donut-body">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={goalSlices} dataKey="value" innerRadius={68} outerRadius={92} paddingAngle={2}>
                        {goalSlices.map((slice) => <Cell key={slice.name} fill={slice.color} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="results-donut-label">{data.goals_pct == null ? '—' : `${data.goals_pct}%`}<span>cumplidos</span></div>
                  <ul>
                    <li><i className="is-met" /> Cumplidos {data.goals_met}</li>
                    <li><i className="is-pending" /> Pendientes {data.goals_pending}</li>
                  </ul>
                </div>
              ) : (
                <p className="results-empty">Todavía no hay objetivos evaluados.</p>
              )}
            </section>
          </div>

          <div className="results-tables">
            <section className="card">
              <h3>Rendimiento por disciplina</h3>
              {data.by_discipline?.length ? (
                <ul className="results-discipline-list">
                  {data.by_discipline.map((row) => (
                    <li key={row.discipline_name}>
                      <div>
                        <strong>{row.discipline_name}</strong>
                        <span>{row.evaluations} evaluaciones</span>
                      </div>
                      <div className="results-discipline-meter">
                        <div className="results-discipline-track">
                          <span style={{ width: `${row.effectiveness || 0}%` }} />
                        </div>
                        <b>{row.effectiveness == null ? '—' : `${row.effectiveness}%`}</b>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="results-empty">Sin datos por disciplina.</p>
              )}
            </section>
            <section className="card">
              <h3>Alumnos que requieren atención</h3>
              {data.attention?.length ? (
                <ul className="results-people">
                  {data.attention.map((row) => (
                    <li key={`att-${row.student_id}`}>
                      <span className="results-avatar">{row.initials}</span>
                      <div>
                        <strong>{row.student_name}</strong>
                        <span>{row.discipline_name} · {row.result}</span>
                      </div>
                      <em className={row.trend < 0 ? 'is-down' : ''}>{row.trend == null ? '' : `${row.trend > 0 ? '+' : ''}${row.trend}%`}</em>
                      <b className={row.status === 'Atención' ? 'is-alert' : 'is-risk'}>{row.status}</b>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="results-empty">Ningún alumno en atención con estos filtros.</p>
              )}
            </section>
            <section className="card">
              <h3>Mejores progresos</h3>
              {data.progress?.length ? (
                <ul className="results-people">
                  {data.progress.map((row, index) => (
                    <li key={`pro-${row.student_id}`}>
                      <span className="results-avatar is-rank">{index + 1}</span>
                      <div>
                        <strong>{row.student_name}</strong>
                        <span>{row.discipline_name}</span>
                      </div>
                      <em className="is-up">+{row.trend}%</em>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="results-empty">Aún no hay mejoras respecto al período anterior.</p>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default PerformanceDashboard;
